import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded"
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import ArticleIcon from "@mui/icons-material/Article"
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined"
import CollectionsIcon from "@mui/icons-material/Collections"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded"
import HorizontalRuleOutlinedIcon from "@mui/icons-material/HorizontalRuleOutlined"
import ImageIcon from "@mui/icons-material/Image"
import LayersIcon from "@mui/icons-material/Layers"
import LayersClearIcon from "@mui/icons-material/LayersClear"
import RectangleOutlinedIcon from "@mui/icons-material/RectangleOutlined"
import SubdirectoryArrowRightRoundedIcon from "@mui/icons-material/SubdirectoryArrowRightRounded"
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined"
import { t } from "i18next"
import React, { ChangeEvent, MutableRefObject, useState } from "react"
import { isWindows } from "react-device-detect"
import type { AnySceneObjectT } from "tweenly"
import { FilterType } from "../../../constants/filterLists"
import { selectUndoable } from "../../../helpers/selector.helpers"
import useMultiselectObject from "../../../hooks/editor/useMultiselectObject"
import useMultiselectObjectAction from "../../../hooks/editor/useMultiselectObjectAction"
import { useObjectActions } from "../../../hooks/useObjectActions"
import { useAppDispatch, useAppSelector, useAppStore } from "../../../hooks/useRedux"
import { useTickerCreateAction } from "../../../hooks/useTickerCreateAction"
import {
    selectIsObjectInSelection,
    selectSelectionCount,
} from "../../../store/slices/activeObject.slice"
import { setLoading } from "../../../store/slices/loading.slice"
import {
    duplicateObjects,
    moveSelectedHereAction,
    moveSelectedToRootAction,
    selectObjectIdsWithStaticDescendant,
    unGroupObjectsAction,
} from "../../../store/slices/objects.slice"
import { selectObjectsRangeAction } from "../../../store/slices/objects.slice/object/selectionActions"
import RightClickContextMenu, { MenuItemT } from "../../common/ContextMenu/RightClickContextMenu"
import AssetsDialog from "./AssetsDialog"
import ImageSrcDialog from "./ImageSrcDialog"
import LoadImageAction from "./LoadImageAction"
import ObjectTitle from "./ObjectTitle"

interface Props {
    object: AnySceneObjectT
    numberOfObjects: number
}

const ObjectsTreeNodeMenu = ({ object, numberOfObjects }: Props) => {
    const [assetType, setAssetType] = useState<FilterType | undefined>()

    const dispatch = useAppDispatch()
    // a count, not the array — switching between single selections leaves the other ~200 tree
    // nodes untouched. The handlers below read the actual ids from the store when they run.
    const store = useAppStore()
    const selectionCount = useAppSelector(selectSelectionCount)
    // a boolean, not the objects map: this menu is mounted once per tree node, so a
    // subscription to the map re-rendered the whole tree on every object edit. The set is
    // memoized, so the subtree walk happens once per objects change, not once per node per
    // dispatched action.
    const isWrapDisabled = useAppSelector((state) =>
        selectObjectIdsWithStaticDescendant(selectUndoable(state).objects).has(object.id)
    )
    const [openImageSrcDialog, setOpenImageSrcDialog] = useState<boolean>(false)
    const { isParentObjectType, addChildObject, deleteObject, moveObjectUp, moveObjectDown } =
        useObjectActions()
    // `useObject()` subscribes to the whole objects map; this menu is mounted once per tree
    // node, so it re-rendered the entire tree on every object edit. Both lookups below happen
    // inside handlers.
    const findObjectById = (id: string) => selectUndoable(store.getState()).objects.value[id]

    // selected itself, or a descendant of a selected object (single or multi selection).
    // One boolean subscription instead of the two wrapper hooks, which measured the DOM for a
    // bounding box this menu never uses.
    const isSelectedObject = useAppSelector((state) => selectIsObjectInSelection(state, object.id))
    // "moveSelectedHere" is disabled on the object that is itself the selection target
    const isFirstSelected = useAppSelector((state) => state.activeObject.selected[0] === object.id)

    const handleMoveObjectUp = () => moveObjectUp(object)
    const handleMoveObjectDown = () => moveObjectDown(object)

    const { handleMultiselectObject } = useMultiselectObject(object)
    const { handleDuplicateObjects, handleGroupObjects, handleDeleteObjects } =
        useMultiselectObjectAction()

    const handleSelectObject = (event: React.MouseEvent) => {
        if (event.shiftKey) {
            dispatch(selectObjectsRangeAction(object.id, "objectsTree"))
            return
        }
        handleMultiselectObject(event.metaKey || event.ctrlKey)
    }

    const handleAddChildItem = () => addChildObject(object.id)
    const handleAddChildEllipse = () => addChildObject(object.id, "ellipse")
    const handleAddChildLine = () => addChildObject(object.id, "line")
    const handleAddChildText = () => addChildObject(object.id, "text")
    const handleDuplicateObject = () => {
        dispatch(duplicateObjects([object]))
    }
    const handleUnGroupObjects = () => {
        dispatch(unGroupObjectsAction({ object }))
    }

    const { addScrollDownTicker, addScrollUpTicker, addScrollUpDownTicker } =
        useTickerCreateAction()
    const { addCrawlContinuousTicker, addCrawlStaggeredTicker } = useTickerCreateAction()

    //move object to root layer of objects
    const handleMoveToRoot = () => {
        const firstSelectedId = store.getState().activeObject.selected[0]
        const selectedObject: AnySceneObjectT | undefined = findObjectById(firstSelectedId)
        if (selectedObject) dispatch(moveSelectedToRootAction({ object: selectedObject }))
    }

    //Add child under parent || move selected object under new parent object
    const handleMoveSelectedHere = () => {
        const firstSelectedId = store.getState().activeObject.selected[0]
        if (!firstSelectedId) return
        const selectedObject: AnySceneObjectT | undefined = findObjectById(firstSelectedId)
        if (selectedObject)
            dispatch(moveSelectedHereAction({ object: selectedObject, parent: object }))
    }

    const handleDeleteObject = () => {
        deleteObject(object)
    }

    // reference to the hidden file input element
    const hiddenSequenceFileInput: MutableRefObject<HTMLInputElement | null> = React.useRef(null)
    const hiddenImageFileInput: MutableRefObject<HTMLInputElement | null> = React.useRef(null)

    const handleReadSequence = async (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(setLoading(true))
        await readImageSequence(e.target.files!, object.id)
        dispatch(setLoading(false))
    }

    const handleReadImage = async (e: ChangeEvent<HTMLInputElement>) => {
        dispatch(setLoading(true))
        await readImageFile(e.target.files?.[0]!, object.id)
        dispatch(setLoading(false))
    }

    const getObjectMenuItems = (): MenuItemT[] => {
        const isParentObject = isParentObjectType(object.type)
        const isImage = object.type === "image" || object.type === "sequence"
        const menuItems: MenuItemT[] =
            selectionCount <= 1
                ? [
                      {
                          icon: <RectangleOutlinedIcon />,
                          title: t("editor:addNewRectangle"),
                          onClick: handleAddChildItem,
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <CircleOutlinedIcon />,
                          title: t("editor:addNewEllipse"),
                          onClick: handleAddChildEllipse,
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <HorizontalRuleOutlinedIcon />,
                          title: t("editor:addNewLine"),
                          onClick: handleAddChildLine,
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <TextFieldsOutlinedIcon />,
                          title: t("editor:addNewText"),
                          onClick: handleAddChildText,
                          divider: true,
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <ImageIcon />,
                          title: t("editor:addNewImage"),
                          onClick: () => hiddenImageFileInput.current?.click(),
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <ImageIcon />,
                          title: t("editor:insertImageUrl"),
                          onClick: () => setOpenImageSrcDialog(true),
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <ImageIcon />,
                          title: t("editor:chooseImageAsset"),
                          onClick: () => setAssetType("image"),
                          divider: true,
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <CollectionsIcon />,
                          title: t("editor:addNewSequence"),
                          onClick: () => hiddenSequenceFileInput.current?.click(),
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <CollectionsIcon />,
                          title: t("editor:chooseSequenceAsset"),
                          onClick: () => setAssetType("sequence"),
                          divider: true,
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <ArticleIcon />,
                          title: t("editor:tickers.addScrollDown"),
                          onClick: () => addScrollDownTicker(object.id),
                          divider: false,
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <ArticleIcon />,
                          title: t("editor:tickers.addScrollUp"),
                          onClick: () => addScrollUpTicker(object.id),
                          divider: false,
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <ArticleIcon />,

                          title: t("editor:tickers.addScrollUpDown"),
                          onClick: () => addScrollUpDownTicker(object.id),
                          divider: true,
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <ArticleIcon />,
                          title: t("editor:tickers.addCrawlContinuous"),
                          onClick: () => addCrawlContinuousTicker(object.id),
                          divider: false,
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <ArticleIcon />,
                          title: t("editor:tickers.addCrawlStaggered"),
                          onClick: () => addCrawlStaggeredTicker(object.id),
                          divider: true,
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <LayersClearIcon />,
                          title: t("editor:unWrap"),
                          shortCut: isWindows ? "Ctrl Shift G" : "⇧ ⌘ G",
                          onClick: handleUnGroupObjects,
                          hidden: !object.isGroup,
                      },
                      {
                          icon: <LayersIcon />,
                          title: t("editor:wrap"),
                          shortCut: isWindows ? "Ctrl G" : "⌘ G",
                          onClick: handleGroupObjects,
                          disabled: isWrapDisabled,
                      },
                      {
                          icon: <ContentCopyIcon />,
                          title: t("editor:duplicate"),
                          shortCut: isWindows ? "Ctrl D" : "⌘ D",
                          onClick: handleDuplicateObject,
                          divider: true,
                      },
                      {
                          icon: <ArrowUpwardIcon />,
                          title: t("editor:moveUp"),
                          shortCut: isWindows ? "Ctrl Shift ▲" : "⇧ ⌘ ▲", //alternative 'Ctrl Shift ↑' : '⇧ ⌘ ↑'
                          onClick: handleMoveObjectUp,
                          disabled: object.index === 0 ? true : false,
                      },
                      {
                          icon: <ArrowDownwardIcon />,
                          title: t("editor:moveDown"),
                          shortCut: isWindows ? "Ctrl Shift ▼" : "⇧ ⌘ ▼", //alternative 'Ctrl Shift ↓' | '⇧ ⌘ ↓'
                          onClick: handleMoveObjectDown,
                          disabled: object.index < numberOfObjects - 1 ? false : true,
                      },
                      {
                          icon: <ArrowBackRoundedIcon />,
                          title: t("editor:moveToTheRoot"),
                          onClick: handleMoveToRoot,
                          disabled: !object.parentId,
                          hidden: !isParentObject,
                      },
                      {
                          icon: <SubdirectoryArrowRightRoundedIcon />,
                          title: t("editor:moveSelectedHere"),
                          onClick: handleMoveSelectedHere,
                          disabled: isFirstSelected || selectionCount === 0 || selectionCount > 1,
                          divider: true,
                          hidden: !isParentObject || isImage,
                      },
                      {
                          icon: <ArrowBackRoundedIcon />,
                          title: t("editor:moveToTheRoot"),
                          onClick: handleMoveToRoot,
                          disabled: !object.parentId,
                          divider: true,
                          hidden: isParentObject,
                      },
                      {
                          icon: <DeleteRoundedIcon />,
                          title: t("editor:delete"),
                          shortCut: isWindows ? "Ctrl Del" : "⌘ ⌫",
                          onClick: handleDeleteObject,
                          color: "error",
                      },
                  ]
                : [
                      {
                          icon: <LayersIcon />,
                          title: t("editor:wrapSelected"),
                          shortCut: isWindows ? "Ctrl G" : "⌘ G",
                          onClick: handleGroupObjects,
                          disabled: isWrapDisabled,
                      },
                      {
                          icon: <ContentCopyIcon />,
                          title: t("editor:duplicateSelected"),
                          shortCut: isWindows ? "Ctrl D" : "⌘ D",
                          onClick: handleDuplicateObjects,
                          divider: true,
                      },
                      {
                          icon: <DeleteRoundedIcon />,
                          title: t("editor:deleteSelected"),
                          shortCut: isWindows ? "Ctrl Del" : "⌘ ⌫",
                          onClick: handleDeleteObjects,
                          color: "error",
                      },
                  ]

        return menuItems.filter((item) => !item.hidden)
    }

    const { readImageSequence, readImageFile } = useObjectActions()

    return (
        <div style={{ minWidth: 0, overflow: "hidden" }}>
            <RightClickContextMenu
                menuLabel={
                    <ObjectTitle
                        index={object.index}
                        title={object.title}
                        isActive={isSelectedObject}
                        handleSelectObject={handleSelectObject}
                    />
                }
                // a function: the items are only built once the menu is opened
                data={getObjectMenuItems}
            />
            <LoadImageAction hiddenFileInputRef={hiddenImageFileInput} onChange={handleReadImage} />
            <ImageSrcDialog
                open={openImageSrcDialog}
                onClose={() => setOpenImageSrcDialog(false)}
                parentId={object.id}
            />
            <LoadImageAction
                hiddenFileInputRef={hiddenSequenceFileInput}
                onChange={handleReadSequence}
                multiple={true}
            />
            <AssetsDialog
                open={Boolean(assetType)}
                onClose={() => setAssetType(undefined)}
                parentId={object.id}
                type={assetType!}
            />
        </div>
    )
}

export default ObjectsTreeNodeMenu
